import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFlow } from '../context/FlowContext';
import useWidgetStore from '../store/useWidgetStore';
import useDataStore from '../store/useDataStore';
/**
 * Select the one and only apptype on the first view, so the consumer doesn't have to click it.
 *
 * Applies to exactly one situation, and nothing else:
 * - the picker is rendered by the select1 route (not select, or any other view), and
 * - that view is the one actually on screen, and
 * - there is a single apptype to choose from, and
 * - nothing is selected yet.
 *
 * Used by ObjectListPicker (source apptype), which is also what ObjectGroupedListPicker renders
 * for every group. Select.tsx clears the selection a moment after arriving on select1, so this
 * reacts to the selection being empty rather than running once.
 *
 * @param enabled whether this picker is one that may auto select (source apptype)
 * @param apptypeIds the apptypes this picker offers
 */
export default function useAutoSelectSingleApptype(enabled, apptypeIds) {
    var _a;
    const flow = useFlow();
    const { setValue } = useFormContext();
    const { selectedApptype, setSelectedApptype, setSelectedGroup } = useWidgetStore();
    const { apptypes } = useDataStore();
    const route = (_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.route;
    const hasSelection = !!selectedApptype && selectedApptype.length > 0;
    // one apptype in this list, and one in the whole widget: a grouped list renders a list per
    // group, and a single apptype in a group says nothing when there are other groups
    const onlyApptypeId = apptypeIds.length === 1 && apptypes.length === 1 ? apptypeIds[0] : 0;
    useEffect(() => {
        var _a;
        if (!enabled || route !== 'select1' || hasSelection || !onlyApptypeId) {
            return;
        }
        // SlideRoutes keeps a view that was left behind mounted, with a frozen flow, so its own
        // route still says select1. Only the raw hash tells whether select1 is on screen right now
        // (same check as Select.tsx).
        if (window.location.hash.substring(12).split('/')[0] !== 'select1') {
            return;
        }
        setSelectedApptype(onlyApptypeId);
        setValue('apptype', [onlyApptypeId]);
        // picking an apptype implies its group, see ObjectListPicker.handleClick
        const parentId = (_a = apptypes.find((apptype) => apptype.id === onlyApptypeId)) === null || _a === void 0 ? void 0 : _a.parentId;
        if (parentId !== undefined) {
            setSelectedGroup(parentId);
        }
    }, [enabled, route, hasSelection, onlyApptypeId, apptypes]);
}
